import { OkDialog } from '@/components/molecules/dialogs/OkDialog';
import { SuccessDialog } from '@/components/molecules/dialogs/SuccessDialog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useTranslation } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { PasswordSetScreen } from '@/modules/auth/components';
import { useForgotPassword } from '@/modules/onboarding/account-recovery/forgot-password/hooks';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';

export default function SetPasswordRoute() {
    const { t } = useTranslation();
    const params = useLocalSearchParams();
    const isForgotPassword = params.forgot === 'true';
    const transactionId = params.transactionId as string | undefined;
    const oid = params.oid as string | undefined;
    const otp = params.otp as string | undefined;
    const email = params.email as string | undefined;
    const { setForgotPassword, error, clearError, resettingPassword: loading } = useForgotPassword();
    const [errorDialogVisible, setErrorDialogVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successDialogVisible, setSuccessDialogVisible] = useState(false);

    const handlePasswordSet = async (password: string) => {
        logger.info('SetPassword', 'Password set', { 
            isForgotPassword, 
            hasPassword: !!password, 
            transactionId,
            hasOid: !!oid,
            hasOtp: !!otp,
            hasEmail: !!email,
        });

        if (isForgotPassword) {
            // Validate all required parameters for set-forgot-password API
            if (!transactionId || !oid || !otp || !email) {
                const missingParams = [];
                if (!transactionId) missingParams.push('transactionId');
                if (!oid) missingParams.push('oid');
                if (!otp) missingParams.push('otp');
                if (!email) missingParams.push('email');
                setErrorMessage(`Missing required parameters: ${missingParams.join(', ')}. Please start the password reset process again.`);
                setErrorDialogVisible(true);
                logger.error('SetPassword', 'Missing required parameters', { transactionId, oid, otp, email });
                return;
            }

            const success = await setForgotPassword(email, otp, transactionId, oid, password);

            if (success) {
                logger.info('SetPassword', 'Password set successfully');
                trackCatalogEvent({
                    screen: DT_SCREEN.SET_NEW_PASSWORD,
                    actionName: 'forgot_password_set_success',
                    eventType: 'action',
                });
                setSuccessDialogVisible(true);
            } else {
                logger.error('SetPassword', 'Failed to set password', { error });
                trackCatalogEvent({
                    screen: DT_SCREEN.SET_NEW_PASSWORD,
                    actionName: 'forgot_password_set_password_error',
                    eventType: 'error',
                    properties: { p_failedMessage: error || t('errors.unknownError') },
                });
                setErrorMessage(error || t('errors.unknownError'));
                setErrorDialogVisible(true);
            }
        } else {
            // For set mode (first time login), just call the callback
            // The parent component should handle the API call
            logger.info('SetPassword', 'Set password mode - password set', { hasPassword: !!password });
        }
    };

    const handleSuccessDialogClose = () => {
        setSuccessDialogVisible(false);
        // Navigate back to previous screen (login screen for forgot password flow)
        router.back();
    };

    return (
        <>
            <PasswordSetScreen
                mode={isForgotPassword ? 'forgot' : 'set'}
                onPasswordSet={handlePasswordSet}
                loading={isForgotPassword ? loading : false}
            />
            <OkDialog
                visible={errorDialogVisible}
                message={errorMessage}
                onOk={() => {
                    setErrorDialogVisible(false);
                    clearError();
                }}
            />
            <SuccessDialog
                visible={successDialogVisible}
                title={t('auth.passwordChangedSuccessfully')}
                onAutoClose={handleSuccessDialogClose}
            />
        </>
    );
}

